import { KEY_FAMILIES } from "@custos/chain-adapters/keys";
import { decryptBytes, encryptBytes, DEFAULT_KDF } from "./crypto.js";
import { parsePrivateKey, privateKeyToHex, vaultAccounts, VaultInputError, } from "./secret.js";
export const MIN_VAULT_PASSWORD_LENGTH = 10;
/** null = fine; otherwise a message to show. */
export function vaultPasswordProblem(password) {
    if (password.length < MIN_VAULT_PASSWORD_LENGTH) {
        return `Use at least ${MIN_VAULT_PASSWORD_LENGTH} characters.`;
    }
    if (password.trim() !== password)
        return "The password can't start or end with a space.";
    return null;
}
function families(secret) {
    return secret.kind === "mnemonic" ? [...KEY_FAMILIES] : KEY_FAMILIES.filter((f) => secret.keys[f]);
}
function aad(network, secret) {
    return `custos-vault:v1:${network}:${secret.kind}:${secret.families.join(",")}`;
}
function serialize(secret) {
    return new TextEncoder().encode(JSON.stringify(secret));
}
function deserialize(bytes) {
    const value = JSON.parse(new TextDecoder().decode(bytes));
    if (value.kind !== "mnemonic" && value.kind !== "privateKeys")
        throw new Error("Unknown vault contents");
    return value;
}
async function seal(network, secret, password, createdAt, opts) {
    const problem = vaultPasswordProblem(password);
    if (problem)
        throw new VaultInputError(problem);
    const label = { kind: secret.kind, families: families(secret) };
    if (label.families.length === 0)
        throw new VaultInputError("The vault has no keys.");
    const bytes = serialize(secret);
    try {
        const blob = await encryptBytes(bytes, password, aad(network, label), opts.kdf ?? DEFAULT_KDF);
        return { format: "custos-vault", version: 1, network, createdAt, ...label, blob };
    }
    finally {
        bytes.fill(0);
    }
}
/** Saves a NEW vault. Refuses if this device already has one for the network (one vault per device). */
export async function createVault(store, network, secret, password, opts = {}) {
    if (await store.get(network))
        throw new VaultInputError("This device already has a vault. Remove it first.");
    const accounts = vaultAccounts(secret, network);
    await store.put(await seal(network, secret, password, new Date().toISOString(), opts));
    return { secret, accounts };
}
/** Decrypts the vault. Throws WrongPasswordError on a wrong password. */
export async function unlockVault(store, network, password) {
    const stored = await store.get(network);
    if (!stored)
        throw new Error("No vault on this device");
    if (stored.network !== network)
        throw new Error("This vault belongs to another network");
    const bytes = await decryptBytes(stored.blob, password, aad(network, stored));
    try {
        const secret = deserialize(bytes);
        return { secret, accounts: vaultAccounts(secret, network) };
    }
    finally {
        bytes.fill(0);
    }
}
export async function changeVaultPassword(store, network, oldPassword, newPassword, opts = {}) {
    const stored = await store.get(network);
    const { secret } = await unlockVault(store, network, oldPassword);
    await store.put(await seal(network, secret, newPassword, stored.createdAt, opts));
}
/** Adds (or replaces) the private key of one chain family in a private-key vault. */
export async function addPrivateKey(store, network, password, family, input, opts = {}) {
    const stored = await store.get(network);
    const { secret } = await unlockVault(store, network, password);
    if (secret.kind !== "privateKeys") {
        throw new VaultInputError("This vault uses a recovery phrase; it already has every chain.");
    }
    const key = parsePrivateKey(family, input, network);
    const next = { kind: "privateKeys", keys: { ...secret.keys, [family]: privateKeyToHex(key) } };
    key.fill(0);
    await store.put(await seal(network, next, password, stored.createdAt, opts));
    return { secret: next, accounts: vaultAccounts(next, network) };
}
/** Builds the secret for a single imported private key. */
export function privateKeySecret(family, input, network) {
    const key = parsePrivateKey(family, input, network);
    try {
        return { kind: "privateKeys", keys: { [family]: privateKeyToHex(key) } };
    }
    finally {
        key.fill(0);
    }
}
/** Deletes the vault from this device. Funds stay on chain; only the recovery phrase/keys can bring them back. */
export async function removeVault(store, network) {
    await store.delete(network);
}
//# sourceMappingURL=vault.js.map